import React, { useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import PromocodeTextInputComponent from "../../../common/PromocodeTextInputComponent";
import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

type Props = {
  onEditStep?: (key: string) => void;
  handleApplyPromoCode?: () => void;
  updatePromocode?: (text: string) => void;
  clearPromoCode?: () => void;
  promoCode?: string;
  errorText?: string;
  testID?: string | undefined;
};

export default function MapiReviewAndEditStep({
  onEditStep,
  handleApplyPromoCode,
  updatePromocode,
  clearPromoCode,
  promoCode,
  errorText,
  testID,
}: Props) {
  const { t, i18n } = useTranslation();

  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption
  );
  const selectedCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode
  );
  const selectedCountryName = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryName
  );
  const selectedStateName = useSessionStore(
    (s: SessionState) => s.selectedDestinationStateName
  );

  const isPromoActivityIndicator = useSessionStore(
    (s: SessionState) => (s as any).isPromoActivityIndicator
  );
  const isPromocodeApplied = useSessionStore(
    (s: SessionState) => (s as any).isPromocodeApplied
  );

  const option: any = pendingSendOption || null;

  const summary = useMemo(() => {
    if (!option) {
      return {
        sendAmount: 0,
        sendCurrency: "USD",
        receiveAmount: 0,
        receiveCurrency: "—",
        serviceOptionName: "—",
      };
    }
    return {
      sendAmount: Number(option?.sendAmount?.amount?.value ?? 0),
      sendCurrency: option?.sendAmount?.amount?.currencyCode || "USD",
      receiveAmount: Number(option?.receiveAmount?.amount?.value ?? 0),
      receiveCurrency: option?.receiveAmount?.amount?.currencyCode || "—",
      serviceOptionName: option?.serviceOptionName || "—",
    };
  }, [pendingSendOption]);

  const alpha2 = useMemo(() => {
    const a3 = (selectedCountryCode || "").toString().toUpperCase();
    return countries.alpha3ToAlpha2(a3) || "";
  }, [selectedCountryCode]);

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>

      {/* You Send / They Receive */}
      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>
              {summary.sendAmount.toFixed(2)}
            </Text>
            <Text style={styles.currencyText}>{summary.sendCurrency}</Text>
          </View>
          <Text style={[styles.inputLabel, { marginTop: 8 }]}>
            {t("REVIEW_THEY_RECEIVE")}
          </Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>
              {summary.receiveAmount.toFixed(2)}
            </Text>
            <Text style={styles.currencyText}>
              {summary.receiveCurrency}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          testID="mapiEditAmount"
          style={styles.editButton}
          onPress={() => onEditStep?.("amount")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {/* Destination Country */}
      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>
            {t("REVIEW_DESTINATION_COUNTRY")}
          </Text>
          <View style={[styles.amountInput, { alignItems: "center" }]}>
            {Platform.OS === "web" ? (
              <View style={styles.flagOverflow}>
                {alpha2 ? (
                  <ReactCountryFlag
                    countryCode={alpha2}
                    svg
                    style={{ width: 30, height: 30, borderRadius: 50 }}
                    title={alpha2}
                  />
                ) : (
                  <Text style={{ fontSize: 16 }}>🌍</Text>
                )}
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )}
            <Text style={styles.countryText}>{selectedCountryName}</Text>
          </View>
        </View>
        <TouchableOpacity
          testID="mapiEditCountry"
          style={styles.editButton}
          onPress={() => onEditStep?.("country")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {/* Destination State (USA / CAN only) */}
      {(selectedCountryCode === "USA" || selectedCountryCode === "CAN") && (
        <View style={styles.sectionRow}>
          <View>
            <Text style={styles.inputLabel}>{t("DESTINATION_STATE")}</Text>
            <Text style={styles.countryText}>{selectedStateName}</Text>
          </View>
          <TouchableOpacity
            testID="mapiEditState"
            style={styles.editButton}
            onPress={() => onEditStep?.("state")}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* How They Receive */}
      <View style={[styles.sectionRow, { marginBottom: 24 }]}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>
            {t("REVIEW_HOW_THEY_RECEIVE")}
          </Text>
          <Text style={[styles.pickUpText, { flexShrink: 1 }]}>
            {summary.serviceOptionName}
          </Text>
        </View>
        <TouchableOpacity
          testID="mapiEditHowTheyReceive"
          style={styles.editButton}
          onPress={() => onEditStep?.("howTheyReceive")}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <PromocodeTextInputComponent
        testID="mapiReviewAndEdit"
        label={t("PROMO_CODE")}
        value={promoCode}
        onChangeText={updatePromocode || (() => {})}
        clearPromoCode={clearPromoCode}
        handleApplyPromoCode={handleApplyPromoCode}
        activityIndicatorStatus={isPromoActivityIndicator}
        isPromocodeApplied={isPromocodeApplied}
        errorText={errorText}
      />

      {/* Terms */}
      <View style={{ marginTop: 16, marginBottom: 16 }}>
        {i18n.language === "en" ? (
          <View>
            <Text style={styles.terms}>
              I agree to MoneyGram's{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Terms and Conditions
              </Text>{" "}
              and confirm I am the sole beneficial owner of the assets, and am
              acting solely on my own profile. By continuing with this
              transaction I consent to the collection, use, disclosure, and
              transfer (including cross-border transfer) of my personal
              information as described in our{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Privacy Policy.
              </Text>
            </Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              <Text style={styles.bold}>Exchange rate</Text>
              {"\n"}
              In addition to transfer fees, a currency exchange rate, also
              determined by MoneyGram, may also apply. MoneyGram may also make
              money from currency exchange. When selecting a money transfer
              operator, carefully compare both transfer fees and currency
              exchange rates. Transfer fees may vary depending on the amount
              being transferred and the delivery method. Transfer fee and
              currency exchange rate may be subject to change by MoneyGram
              without notice.
            </Text>
          </View>
        ) : (
          <View>
            <Text style={styles.terms}>{t("TERMS_AND_CONDITIONS")}</Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              {t("TERMS_EXCHANGE_POLICY")}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    justifyContent: "flex-start",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
  },
  sectionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    marginRight: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: "stretch",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 24,
    paddingLeft: 8,
  },
  editButton: {
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  editText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 16,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  countryText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  pickUpText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.black,
    lineHeight: 20,
  },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: {
    fontWeight: "600",
  },
});
